/**
 * Line icons, drawn at a 24-unit grid and scaled by the `size` prop.
 *
 * Inline rather than from a package: there are two dozen of them, the whole file
 * is smaller than the dependency's index, and nothing here needs a tree shake to
 * stay small.
 */

type Props = { size?: number; className?: string };

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
});

export function SearchIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function ChevronIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function ArrowIcon({ size = 14, className, direction = "right" }: Props & { direction?: "left" | "right" }) {
  return (
    <svg {...base(size)} className={className} style={{ transform: direction === "left" ? "rotate(180deg)" : undefined }}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function CalendarIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

export function CheckIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className} strokeWidth={2.6}>
      <path d="m5 12.5 4.5 4.5L19 7" />
    </svg>
  );
}

export function CloseIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className} strokeWidth={2.2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

export function PlusIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className} strokeWidth={2.1}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function RefreshIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20 11a8 8 0 1 0-.8 4.5" />
      <path d="M20 4v7h-7" />
    </svg>
  );
}

export function ExternalIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

export function SheetIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M4 9h16M4 15h16M10 9v12" />
    </svg>
  );
}

export function PinIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 17v5" />
      <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
    </svg>
  );
}

export function TrashIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V4h6v3" />
    </svg>
  );
}

export function CameraIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.3 4.7h5.4a1 1 0 0 1 .83.45l.94 1.4A1 1 0 0 0 17.3 7h2.2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9Z" />
      <circle cx="12" cy="13" r="3.4" />
    </svg>
  );
}

export function UploadIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
      <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
    </svg>
  );
}

export function EditIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 20h4L20 8l-4-4L4 16v4Z" />
      <path d="m14 6 4 4" />
    </svg>
  );
}

export function SettingsIcon({ size = 15, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.5 12a7.5 7.5 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7.6 7.6 0 0 0-2-1.2L14.7 3h-3.9l-.4 2.5a7.6 7.6 0 0 0-2 1.2l-2.3-.9-2 3.4 2 1.5a7.6 7.6 0 0 0 0 2.5l-2 1.5 2 3.4 2.3-.9c.6.5 1.3.9 2 1.2l.4 2.6h3.9l.4-2.6c.7-.3 1.4-.7 2-1.2l2.3.9 2-3.4-2-1.5c.1-.4.1-.8.1-1.2Z" />
    </svg>
  );
}

export function SignOutIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M15 12H4M11 8l-4 4 4 4" />
      <path d="M9 5V4a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-1" />
    </svg>
  );
}

export function SparkIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
    </svg>
  );
}

export function LinkIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M10 13a4 4 0 0 0 5.7.4l2.6-2.6a4 4 0 0 0-5.7-5.7l-1.5 1.5" />
      <path d="M14 11a4 4 0 0 0-5.7-.4l-2.6 2.6a4 4 0 0 0 5.7 5.7l1.5-1.5" />
    </svg>
  );
}

export function ChartIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

export function ClipboardIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1M9 10h6M9 14h4" />
    </svg>
  );
}

export function FlameIcon({ size = 12, className }: Props) {
  return (
    <svg {...base(size)} className={className} fill="currentColor" stroke="none">
      <path d="M12 2c.4 3 -1.6 4.2 -2.8 5.6C8 9 7.4 10.2 7.4 11.8a4.6 4.6 0 0 0 9.2 0c0 -2.4 -1.4 -3.6 -2.4 -5 -.7 -1 -1.2 -2 -1.2 -3.2 0 -.6 -.4 -1.3 -1 -1.6Zm0 11.2c.9.7 1.4 1.5 1.4 2.4a1.5 1.5 0 1 1 -3 0c0 -.9.6 -1.8 1.6 -2.4Z" />
    </svg>
  );
}

export function BellIcon({ size = 14, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

export function FolderIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </svg>
  );
}

/** Sheets stacked behind one another: the duplication step on a flow. */
export function CopiesIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="8" y="3" width="13" height="13" rx="2" />
      <path d="M16 20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1" />
    </svg>
  );
}

/** Four corners pushed outwards: open this bigger. */
export function ExpandIcon({ size = 13, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </svg>
  );
}
